import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SALUTATIONS = ["Mr", "Mrs", "Ms", "M/S"];

/**
 * Standard customer-name block: Salutation + (First/Last name OR Company name).
 *
 * Reads/writes these fields on the parent's `formData`:
 *   - salutation       ("Mr" | "Mrs" | "Ms" | "M/S")
 *   - first_name       (Individual only)
 *   - last_name        (Individual only)
 *   - company_name     (Company only — when salutation = "M/S")
 *
 * The backend auto-derives `customer_type` and `customer_name`.
 */
export function CustomerNameFields({ formData, updateField, required = true, errors = {} }) {

  const isCompany = formData.salutation === "M/S";

  const handleSalutationChange = (v) => {
    updateField("salutation", v);
    if (v === "M/S") {
      updateField("first_name", "");
      updateField("last_name", "");
    } else {
      updateField("company_name", "");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 sm:col-span-3">
        <Label className="form-label">
          Salutation {required && <span className="text-red-500">*</span>}
        </Label>
        <Select value={formData.salutation || ""} onValueChange={handleSalutationChange}>
          <SelectTrigger className="rounded-sm" data-testid="input-salutation">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {SALUTATIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isCompany ? (
        <div className="col-span-12 sm:col-span-9">
          <Label className="form-label">
            Company Name {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            value={formData.company_name || ""}
            onChange={(e) => updateField("company_name", e.target.value)}
            placeholder="Company name"
            className="rounded-sm"
            data-testid="input-company-name"
          />
          {errors.company_name && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-company-name">
              {errors.company_name}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="col-span-12 sm:col-span-4">
            <Label className="form-label">
              First Name {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.first_name || ""}
              onChange={(e) => updateField("first_name", e.target.value)}
              placeholder="First name"
              className="rounded-sm"
              data-testid="input-first-name"
            />
            {errors.first_name && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-first-name">
                {errors.first_name}
              </p>
            )}
          </div>
          <div className="col-span-12 sm:col-span-5">
            <Label className="form-label">
              Last Name {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.last_name || ""}
              onChange={(e) => updateField("last_name", e.target.value)}
              placeholder="Last name"
              className="rounded-sm"
              data-testid="input-last-name"
            />
            {errors.last_name && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-last-name">
                {errors.last_name}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerNameFields;
